import * as Filters from './services/filters';
import { reduxStore } from '../../services/gecko-store/redux';

const defaultFilterState = {
  fetching: false,
  ids: [],
  page: 1,
  nextPage: 1,
  quickFilter: null,
  sort: 'DESC',
};

const defaultFilterStateWithUnreadIds = {
  ...defaultFilterState,
  unreadIds: [],
};

const getSavedCustomFilter = (state, id) =>
  state.savedCustomFilters.find((customFilter) => customFilter.id === id);

const updateSavedCustomFilters = (state, id, updateConfig) => {
  const updatedFilter = {
    ...getSavedCustomFilter(state, id),
    ...updateConfig,
  };
  return state.savedCustomFilters.map((customFilter) => {
    if (customFilter.id !== id) return customFilter;
    return updatedFilter;
  });
};

export const initialState = {
  conversationById: {},
  contactsById: {},
  contactsFetchStatus: {},
  notesByContactId: {},
  notesFetchStatus: {},
  fetchingConversations: {},
  myConversations: defaultFilterStateWithUnreadIds,
  unassignedConversations: defaultFilterStateWithUnreadIds,
  openConversations: defaultFilterState,
  closedConversations: defaultFilterState,
  sentConversations: defaultFilterState,
  botInterferenceConversations: defaultFilterState,
  undeliveredMessagesConversations: defaultFilterState,
  contactFilter: {
    contact: null,
    ...defaultFilterState,
  },
  allForContactFilter: {
    contact: null,
    ...defaultFilterState,
  },
  tagFilter: {
    tag: null,
    ...defaultFilterState,
  },
  savedCustomFilters: [],
  customFilter: {
    count: 0,
    filters: localStorage.getItem('conversation-custom-filter')
      ? JSON.parse(localStorage.getItem('conversation-custom-filter'))
      : [],
    ...defaultFilterStateWithUnreadIds,
  },
  attendancesByContactUlid: {},
  attendanceFetchStatus: {},
  integrationSyncFetchStatus: {},
  integrationSyncByContactId: {},
  rsvpLink: '',
};

// Accessing the redux global state isn't allowed inside reducers so keep a local variable in sync
let loggedInUser = null;
setImmediate(() => {
  loggedInUser = reduxStore.getState().appState.loggedInUser;
  reduxStore.subscribe(() => {
    loggedInUser = reduxStore.getState().appState.loggedInUser;
  });
});

const handleFetchedConversationsForFilter =
  (filter, { addToExisting = false } = {}) =>
  (state, { payload }) => {
    const { data, page, nextPage, sort, perPage, quickFilter } = payload;
    const currentIds = state[filter].ids;
    const dataIds = data.map((convo) => convo.id);
    const ids = addToExisting === true ? currentIds.concat(dataIds) : dataIds;
    const existingFilterState = state[filter];
    const newFilterState = {
      ...existingFilterState,
      quickFilter,
      nextPage,
      page,
      sort,
      ids,
      perPage,
      fetching: false,
    };
    const conversationById = data.reduce(addConvoById, state.conversationById);
    return { ...state, conversationById, [filter]: newFilterState };
  };

const handleFetchedConversationsForFilterWithUnreadIds =
  (filter, { addToExisting = false } = {}) =>
  (state, { payload }) => {
    const { data } = payload;
    const currentUnreadIds = state[filter].unreadIds;
    const dataUnreadIds = data.reduce((unreadIds, convo) => {
      if (convo.unread_count > 0) {
        return unreadIds.concat(convo.id);
      }
      return unreadIds;
    }, []);
    const unreadIds =
      addToExisting === true
        ? currentUnreadIds.concat(dataUnreadIds)
        : dataUnreadIds;

    const newState = handleFetchedConversationsForFilter(filter, {
      addToExisting,
    })(state, { payload });
    const newFilterState = newState[filter];
    const newFilterStateWithUnreadIds = {
      ...newFilterState,
      unreadIds,
    };
    return {
      ...newState,
      [filter]: newFilterStateWithUnreadIds,
    };
  };

const setCount = (statePart, count) => {
  return { ...statePart, count };
};

const addToStatePart = (statePart, conversation) => {
  const ids = [...statePart.ids, conversation.id];
  return { ...statePart, ids };
};

const removeFromStatePart = (statePart, conversation) => {
  const ids = statePart.ids.filter((id) => id !== conversation.id);
  return { ...statePart, ids };
};

const incrementCountConversation = (statePart) => {
  const count = statePart.count !== undefined ? statePart.count + 1 : undefined;
  return { ...statePart, count };
};

const decrementCountConversation = (statePart) => {
  const count = statePart.count !== 0 ? statePart.count - 1 : statePart.count;
  return { ...statePart, count };
};

const UPDATED_CONVERSATION = (state, { payload, metadata }) => {
  const { oldConversation } = metadata;

  const updateStatePartIds = (statePart, filterMatcher) => {
    const converationIsInFilter = filterMatcher(payload);
    const oldConverationIsInFilter = oldConversation
      ? filterMatcher(oldConversation)
      : false;
    const isNewToFilter = converationIsInFilter && !oldConverationIsInFilter;
    const isNoLongerInFilter =
      !converationIsInFilter && oldConverationIsInFilter;
    // If the conversation matches the filter and the previous one didn't then increment the count by 1
    if (isNewToFilter) {
      return addToStatePart(statePart, payload);
    }
    // If the conversation doesnt match the filter and the previous one did then decrement the count by 1
    if (isNoLongerInFilter) {
      return removeFromStatePart(statePart, payload);
    }
    // Otherwise no change to the state
    return statePart;
  };

  const updateStatePartCount = (statePart, filterMatcher) => {
    const converationIsInFilter = filterMatcher(payload);
    const oldConverationIsInFilter = oldConversation
      ? filterMatcher(oldConversation)
      : false;
    const isNewToFilter = converationIsInFilter && !oldConverationIsInFilter;
    const isNoLongerInFilter =
      !converationIsInFilter && oldConverationIsInFilter;
    // If the conversation matches the filter and the previous one didn't then increment the count by 1
    if (isNewToFilter) {
      return incrementCountConversation(statePart);
    }
    // If the conversation doesnt match the filter and the previous one did then decrement the count by 1
    if (isNoLongerInFilter) {
      return decrementCountConversation(statePart);
    }
    // Otherwise no change to the state
    return statePart;
  };

  const updateStatePart = (statePart, filterMatcher) => {
    const quickFilterMatcher = Filters.withQuickFilter(
      filterMatcher,
      statePart.quickFilter
    );
    const withUpdatedIds = updateStatePartIds(statePart, quickFilterMatcher);
    const withUpdatedCount = updateStatePartCount(
      withUpdatedIds,
      filterMatcher
    );
    return {
      ...statePart,
      ...withUpdatedCount,
    };
  };

  const myConversations = updateStatePart(
    state.myConversations,
    Filters.myConversations(loggedInUser.id)
  );
  const unassignedConversations = updateStatePart(
    state.unassignedConversations,
    Filters.unassignedConversations
  );
  const openConversations = updateStatePart(
    state.openConversations,
    Filters.openConversations
  );
  const closedConversations = updateStatePart(
    state.closedConversations,
    Filters.closedConversations
  );
  const botInterferenceConversations = updateStatePart(
    state.botInterferenceConversations,
    Filters.assignedToBotConversations
  );
  const sentConversations = updateStatePart(
    state.sentConversations,
    Filters.sentConversations
  );
  const undeliveredMessagesConversations = updateStatePart(
    state.undeliveredMessagesConversations,
    Filters.undeliveredMessagesConversations
  );
  // Only update the custom filter if the custom filters are set up.
  const customFilter = state.customFilter.filters.length
    ? updateStatePart(
        state.customFilter,
        Filters.customFilterConversations(state.customFilter.filters)
      )
    : state.customFilter;

  const savedCustomFilters = state.savedCustomFilters.map((customFilter) =>
    updateStatePart(
      customFilter,
      Filters.customFilterConversations(customFilter.filters)
    )
  );

  const contactFilter = updateStatePart(
    state.contactFilter,
    Filters.contactConversations(state.contactFilter.contact)
  );
  const allForContactFilter = updateStatePart(
    state.allForContactFilter,
    Filters.allForContactConversations(state.allForContactFilter.contact)
  );
  const tagFilter = updateStatePart(
    state.tagFilter,
    Filters.tagConversations(state.tagFilter.tag)
  );

  const conversationById = { ...state.conversationById, [payload.id]: payload };

  return {
    ...state,
    conversationById,
    myConversations,
    unassignedConversations,
    openConversations,
    closedConversations,
    botInterferenceConversations,
    sentConversations,
    undeliveredMessagesConversations,
    customFilter,
    savedCustomFilters,
    contactFilter,
    allForContactFilter,
    tagFilter,
  };
};

const addConvoById = (conversationById, convo) => ({
  ...conversationById,
  [convo.id]: convo,
});

const DELETED_CONVERSATION = (state, { payload }) => {
  const removeFromStatePartById = (statePart, conversationId) => {
    const index = statePart.ids.indexOf(conversationId);
    if (index === -1) {
      return statePart;
    }
    const ids = [
      ...statePart.ids.slice(0, index),
      ...statePart.ids.slice(index + 1, statePart.ids.length),
    ];
    const count =
      statePart.count !== undefined ? statePart.count - 1 : undefined;
    return { ...statePart, ids, count };
  };

  const removeFromStatePartIfMatches = (statePart, matcher, conversation) => {
    if (matcher(conversation)) {
      const withIdRemoved = removeFromStatePart(statePart, conversation);
      return decrementCountConversation(withIdRemoved);
    }
    return statePart;
  };

  const removeFromStateById = (state, id) => {
    const conversationById = { ...state.conversationById, [id]: undefined };
    const myConversations = removeFromStatePartById(state.myConversations, id);
    const unassignedConversations = removeFromStatePartById(
      state.unassignedConversations,
      id
    );
    const openConversations = removeFromStatePartById(
      state.openConversations,
      id
    );
    const closedConversations = removeFromStatePartById(
      state.closedConversations,
      id
    );
    const botInterferenceConversations = removeFromStatePartById(
      state.botInterferenceConversations,
      id
    );
    const sentConversations = removeFromStatePartById(
      state.sentConversations,
      id
    );
    const undeliveredMessagesConversations = removeFromStatePartById(
      state.undeliveredMessagesConversations,
      id
    );
    const customFilter = removeFromStatePartById(state.customFilter, id);
    const savedCustomFilters = state.savedCustomFilters.map((customFilter) =>
      removeFromStatePartById(customFilter, id)
    );
    const contactFilter = removeFromStatePartById(state.contactFilter, id);
    const allForContactFilter = removeFromStatePartById(
      state.allForContactFilter,
      id
    );
    const tagFilter = removeFromStatePartById(state.tagFilter, id);
    return {
      ...state,
      conversationById,
      myConversations,
      unassignedConversations,
      openConversations,
      closedConversations,
      botInterferenceConversations,
      sentConversations,
      undeliveredMessagesConversations,
      customFilter,
      savedCustomFilters,
      contactFilter,
      allForContactFilter,
      tagFilter,
    };
  };

  const removeFromState = (state, conversation) => {
    const conversationById = {
      ...state.conversationById,
      [conversation.id]: undefined,
    };
    const myConversations = removeFromStatePartIfMatches(
      state.myConversations,
      Filters.myConversations(loggedInUser.id),
      conversation
    );
    const unassignedConversations = removeFromStatePartIfMatches(
      state.unassignedConversations,
      Filters.unassignedConversations,
      conversation
    );
    const openConversations = removeFromStatePartIfMatches(
      state.openConversations,
      Filters.openConversations,
      conversation
    );
    const closedConversations = removeFromStatePartIfMatches(
      state.closedConversations,
      Filters.closedConversations,
      conversation
    );
    const botInterferenceConversations = removeFromStatePartIfMatches(
      state.botInterferenceConversations,
      Filters.assignedToBotConversations,
      conversation
    );
    const sentConversations = removeFromStatePartIfMatches(
      state.sentConversations,
      Filters.sentConversations,
      conversation
    );
    const undeliveredMessagesConversations = removeFromStatePartIfMatches(
      state.undeliveredMessagesConversations,
      Filters.undeliveredMessagesConversations,
      conversation
    );
    const customFilter = removeFromStatePartIfMatches(
      state.customFilter,
      Filters.customFilterConversations(state.customFilter.filters),
      conversation
    );
    const savedCustomFilters = state.savedCustomFilters.map((customFilter) =>
      removeFromStatePartIfMatches(
        customFilter,
        Filters.customFilterConversations(customFilter.filters),
        conversation
      )
    );
    const contactFilter = removeFromStatePartIfMatches(
      state.contactFilter,
      Filters.contactConversations(state.contactFilter.contact),
      conversation
    );
    const allForContactFilter = removeFromStatePartIfMatches(
      state.allForContactFilter,
      Filters.allForContactConversations(state.allForContactFilter.contact),
      conversation
    );
    const tagFilter = removeFromStatePartIfMatches(
      state.tagFilter,
      Filters.tagConversations(state.tagFilter.tag),
      conversation
    );
    return {
      ...state,
      conversationById,
      myConversations,
      unassignedConversations,
      openConversations,
      closedConversations,
      botInterferenceConversations,
      sentConversations,
      undeliveredMessagesConversations,
      customFilter,
      savedCustomFilters,
      contactFilter,
      allForContactFilter,
      tagFilter,
    };
  };

  if (payload.id) {
    return removeFromState(state, payload);
  } else {
    return removeFromStateById(state, payload);
  }
};

const reductions = {
  STARTED_FETCHING_MY_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    const myConversations = { ...state.myConversations, page, fetching: true };
    return { ...state, myConversations };
  },
  FAILED_FETCHING_MY_CONVERSATIONS: (state) => ({
    ...state,
    myConversations: { ...state.myConversations, fetching: false },
  }),

  FETCHED_MY_CONVERSATIONS:
    handleFetchedConversationsForFilterWithUnreadIds('myConversations'),
  FETCHED_MORE_MY_CONVERSATIONS:
    handleFetchedConversationsForFilterWithUnreadIds('myConversations', {
      addToExisting: true,
    }),

  STARTED_FETCHING_UNASSIGNED_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    return {
      ...state,
      unassignedConversations: {
        ...state.unassignedConversations,
        page,
        fetching: true,
      },
    };
  },
  FAILED_FETCHING_UNASSIGNED_CONVERSATIONS: (state) => ({
    ...state,
    unassignedConversations: {
      ...state.unassignedConversations,
      fetching: false,
    },
  }),
  FETCHED_UNASSIGNED_CONVERSATIONS:
    handleFetchedConversationsForFilterWithUnreadIds('unassignedConversations'),
  FETCHED_MORE_UNASSIGNED_CONVERSATIONS:
    handleFetchedConversationsForFilterWithUnreadIds(
      'unassignedConversations',
      { addToExisting: true }
    ),

  STARTED_FETCHING_OPEN_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    return {
      ...state,
      openConversations: { ...state.openConversations, page, fetching: true },
    };
  },
  FAILED_FETCHING_OPEN_CONVERSATIONS: (state) => ({
    ...state,
    openConversations: { ...state.openConversations, fetching: false },
  }),
  FETCHED_OPEN_CONVERSATIONS:
    handleFetchedConversationsForFilter('openConversations'),
  FETCHED_MORE_OPEN_CONVERSATIONS: handleFetchedConversationsForFilter(
    'openConversations',
    { addToExisting: true }
  ),

  STARTED_FETCHING_CLOSED_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    return {
      ...state,
      closedConversations: {
        ...state.closedConversations,
        page,
        fetching: true,
      },
    };
  },
  FAILED_FETCHING_CLOSED_CONVERSATIONS: (state) => ({
    ...state,
    closedConversations: { ...state.closedConversations, fetching: false },
  }),
  FETCHED_CLOSED_CONVERSATIONS: handleFetchedConversationsForFilter(
    'closedConversations'
  ),
  FETCHED_MORE_CLOSED_CONVERSATIONS: handleFetchedConversationsForFilter(
    'closedConversations',
    { addToExisting: true }
  ),

  STARTED_FETCHING_UNDELIVERED_MESSAGES_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    const undeliveredMessagesConversations = {
      ...state.undeliveredMessagesConversations,
      page,
      fetching: true,
    };
    return { ...state, undeliveredMessagesConversations };
  },
  FAILED_FETCHING_UNDELIVERED_MESSAGES_CONVERSATIONS: (state) => ({
    ...state,
    undeliveredMessagesConversations: {
      ...state.undeliveredMessagesConversations,
      fetching: false,
    },
  }),

  FETCHED_UNDELIVERED_MESSAGES_CONVERSATIONS:
    handleFetchedConversationsForFilter('undeliveredMessagesConversations'),
  FETCHED_MORE_UNDELIVERED_MESSAGES_CONVERSATIONS:
    handleFetchedConversationsForFilter('undeliveredMessagesConversations', {
      addToExisting: true,
    }),

  STARTED_FETCHING_SENT_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    return {
      ...state,
      sentConversations: { ...state.sentConversations, page, fetching: true },
    };
  },
  FAILED_FETCHING_SENT_CONVERSATIONS: (state) => ({
    ...state,
    sentConversations: { ...state.sentConversations, fetching: false },
  }),
  FETCHED_SENT_CONVERSATIONS:
    handleFetchedConversationsForFilter('sentConversations'),
  FETCHED_MORE_SENT_CONVERSATIONS: handleFetchedConversationsForFilter(
    'sentConversations',
    { addToExisting: true }
  ),

  STARTED_FETCHING_BOT_INTERFERENCE_CONVERSATIONS: (state, { payload }) => {
    const { page } = payload;
    return {
      ...state,
      botInterferenceConversations: {
        ...state.botInterferenceConversations,
        page,
        fetching: true,
      },
    };
  },
  FAILED_FETCHING_BOT_INTERFERENCE_CONVERSATIONS: (state) => ({
    ...state,
    botInterferenceConversations: {
      ...state.botInterferenceConversations,
      fetching: false,
    },
  }),
  FETCHED_BOT_INTERFERENCE_CONVERSATIONS: handleFetchedConversationsForFilter(
    'botInterferenceConversations'
  ),
  FETCHED_MORE_BOT_INTERFERENCE_CONVERSATIONS:
    handleFetchedConversationsForFilter('botInterferenceConversations', {
      addToExisting: true,
    }),

  CREATED_CONVERSATION: UPDATED_CONVERSATION,
  UPDATED_CONVERSATION: UPDATED_CONVERSATION,
  DELETE_CONVERSATION: DELETED_CONVERSATION,
  RESTRICTED_CONVERSATION_UNASSIGNED_FROM_ME: DELETED_CONVERSATION,

  CONVERSATION_UNREAD: (state, { payload }) => {
    if (state.myConversations.ids.includes(payload.id)) {
      return {
        ...state,
        myConversations: {
          ...state.myConversations,
          unreadIds: state.myConversations.unreadIds.concat(payload.id),
        },
      };
    }
    if (state.unassignedConversations.ids.includes(payload.id)) {
      return {
        ...state,
        unassignedConversations: {
          ...state.unassignedConversations,
          unreadIds: state.unassignedConversations.unreadIds.concat(payload.id),
        },
      };
    }
    return state;
  },

  CONVERSATION_READ: (state, { payload }) => {
    const myConversationUnreadIds = state.myConversations.unreadIds.filter(
      (id) => id !== payload.id
    );
    const unassignedConversationUnreadIds =
      state.unassignedConversations.unreadIds.filter((id) => id !== payload.id);
    const myConversations = {
      ...state.myConversations,
      unreadIds: myConversationUnreadIds,
    };
    const unassignedConversations = {
      ...state.unassignedConversations,
      unreadIds: unassignedConversationUnreadIds,
    };
    return { ...state, myConversations, unassignedConversations };
  },

  STARTED_FETCHING_CONTACT: (state, { payload }) => {
    const fetchStatus = { fetching: true };
    return {
      ...state,
      contactsFetchStatus: {
        ...state.contactsFetchStatus,
        [payload.id]: fetchStatus,
      },
    };
  },
  STARTED_FETCHING_CONTACTS: (state, { payload }) => {
    return payload.contacts.reduce((state, contactId) => {
      const fetchStatus = { fetching: true };
      const contactsFetchStatus = {
        ...state.contactsFetchStatus,
        [contactId]: fetchStatus,
      };
      return { ...state, contactsFetchStatus };
    }, state);
  },
  FETCHED_CONTACT: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    const contactsFetchStatus = {
      ...state.contactsFetchStatus,
      [payload.id]: fetchStatus,
    };
    const contactsById = { ...state.contactsById, [payload.id]: payload };
    return { ...state, contactsFetchStatus, contactsById };
  },
  FETCHED_CONTACTS: (state, { payload }) => {
    return Object.keys(payload.contacts).reduce((state, contactId) => {
      const fetchStatus = { fetching: false };
      const contactsFetchStatus = {
        ...state.contactsFetchStatus,
        [contactId]: fetchStatus,
      };
      const contactsById = {
        ...state.contactsById,
        [contactId]: payload.contacts[contactId],
      };
      return { ...state, contactsFetchStatus, contactsById };
    }, state);
  },
  FAILED_FETCHING_CONTACT: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    return {
      ...state,
      contactsFetchStatus: {
        ...state.contactsFetchStatus,
        [payload.id]: fetchStatus,
      },
    };
  },

  UPDATED_CONTACT: (state, { payload }) => ({
    ...state,
    contactsById: { ...state.contactsById, [payload.id]: payload },
  }),

  STARTED_FETCHING_CONTACT_NOTES: (state, { payload }) => {
    const fetchStatus = { fetching: true };
    return {
      ...state,
      notesFetchStatus: {
        ...state.notesFetchStatus,
        [payload.contactId]: fetchStatus,
      },
    };
  },
  FETCHED_CONTACT_NOTES: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    const notesFetchStatus = {
      ...state.notesFetchStatus,
      [payload.contactId]: fetchStatus,
    };
    const notesByContactId = {
      ...state.notesByContactId,
      [payload.contactId]: payload.notes,
    };
    return { ...state, notesFetchStatus, notesByContactId };
  },
  FAILED_FETCHING_CONTACT_NOTES: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    return {
      ...state,
      notesFetchStatus: {
        ...state.notesFetchStatus,
        [payload.contactId]: fetchStatus,
      },
    };
  },
  NEW_CONTACT_NOTE: (state, { payload }) => {
    if (!state.notesByContactId[payload.contactId]) {
      return state;
    }
    const notes = state.notesByContactId[payload.contactId].concat(
      payload.note
    );
    const notesByContactId = {
      ...state.notesByContactId,
      [payload.contactId]: notes,
    };
    return { ...state, notesByContactId };
  },
  UPDATED_CONTACT_NOTE: (state, { payload }) => {
    if (!state.notesByContactId[payload.contactId]) {
      return state;
    }
    const notes = state.notesByContactId[payload.contactId].map(
      (existingNote) => {
        if (existingNote.id !== payload.note.id) {
          return existingNote;
        }
        return payload.note;
      }
    );
    const notesByContactId = {
      ...state.notesByContactId,
      [payload.contactId]: notes,
    };
    return { ...state, notesByContactId };
  },
  DELETED_CONTACT_NOTE: (state, { payload }) => {
    if (!state.notesByContactId[payload.contactId]) {
      return state;
    }
    const notes = state.notesByContactId[payload.contactId].filter(
      (existingNote) => {
        return existingNote.id !== payload.note.id;
      }
    );
    const notesByContactId = {
      ...state.notesByContactId,
      [payload.contactId]: notes,
    };
    return { ...state, notesByContactId };
  },

  STARTED_FETCHING_CONTACT_FILTER: (state, { payload }) => ({
    ...state,
    contactFilter: {
      ...state.contactFilter,
      contact: payload.contactId,
      fetching: true,
    },
  }),
  FETCHED_CONTACT_FILTER: handleFetchedConversationsForFilter('contactFilter'),
  FETCHED_MORE_CONTACT_FILTER: handleFetchedConversationsForFilter(
    'contactFilter',
    { addToExisting: true }
  ),
  FAILED_FETCHING_CONTACT_FILTER: (state) => ({
    ...state,
    contactFilter: { ...state.contactFilter, fetching: false },
  }),

  STARTED_FETCHING_ALL_FOR_CONTACT_FILTER: (state, { payload }) => ({
    ...state,
    allForContactFilter: {
      ...state.allForContactFilter,
      contact: payload.contactId,
      fetching: true,
    },
  }),
  FETCHED_ALL_FOR_CONTACT_FILTER: handleFetchedConversationsForFilter(
    'allForContactFilter'
  ),
  FETCHED_MORE_ALL_FOR_CONTACT_FILTER: handleFetchedConversationsForFilter(
    'allForContactFilter',
    { addToExisting: true }
  ),
  FAILED_FETCHING_ALL_FOR_CONTACT_FILTER: (state) => ({
    ...state,
    allForContactFilter: { ...state.allForContactFilter, fetching: false },
  }),

  STARTED_FETCHING_TAG_FILTER: (state, { payload }) => ({
    ...state,
    tagFilter: { ...state.tagFilter, tag: payload.tagId, fetching: true },
  }),

  FETCHED_TAG_FILTER: handleFetchedConversationsForFilter('tagFilter'),
  FETCHED_MORE_TAG_FILTER: handleFetchedConversationsForFilter('tagFilter', {
    addToExisting: true,
  }),
  FAILED_FETCHING_TAG_FILTER: (state) => ({
    ...state,
    tagFilter: { ...state.tagFilter, fetching: false },
  }),

  STARTED_FETCHING_CUSTOM_FILTER: (state, { payload }) => {
    const { page, filters } = payload;
    const customFilter = {
      ...state.customFilter,
      page,
      filters,
      fetching: true,
    };
    return { ...state, customFilter };
  },
  FETCHED_CUSTOM_FILTER:
    handleFetchedConversationsForFilterWithUnreadIds('customFilter'),
  FETCHED_MORE_CUSTOM_FILTER: handleFetchedConversationsForFilterWithUnreadIds(
    'customFilter',
    { addToExisting: true }
  ),
  FAILED_FETCHING_CUSTOM_FILTER: (state) => ({
    ...state,
    customFilter: { ...state.customFilter, fetching: false },
  }),
  STARTED_FETCHING_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id, page, filters } = payload;
    const savedCustomFilters = updateSavedCustomFilters(state, id, {
      page,
      filters,
      fetching: true,
    });
    return { ...state, savedCustomFilters };
  },
  FETCHED_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id } = payload;
    const savedCustomFilter = getSavedCustomFilter(state, id);
    const updatedFilter = handleFetchedConversationsForFilterWithUnreadIds(id)(
      { [id]: savedCustomFilter, conversationById: state.conversationById },
      { payload }
    );

    const savedCustomFilters = updateSavedCustomFilters(
      state,
      id,
      updatedFilter[id]
    );
    return {
      ...state,
      savedCustomFilters,
      conversationById: updatedFilter.conversationById,
    };
  },
  FETCHED_MORE_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id } = payload;
    const savedCustomFilter = getSavedCustomFilter(state, id);
    const updatedFilter = handleFetchedConversationsForFilterWithUnreadIds(id, {
      addToExisting: true,
    })(
      { [id]: savedCustomFilter, conversationById: state.conversationById },
      { payload }
    );

    const savedCustomFilters = updateSavedCustomFilters(
      state,
      id,
      updatedFilter[id]
    );
    return {
      ...state,
      savedCustomFilters,
      conversationById: updatedFilter.conversationById,
    };
  },
  FAILED_FETCHING_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id } = payload;
    const savedCustomFilters = updateSavedCustomFilters(state, id, {
      fetching: false,
    });
    return { ...state, savedCustomFilters };
  },
  PINNED_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id } = payload;
    const savedCustomFilters = state.savedCustomFilters.map((customFilter) => {
      if (customFilter.id !== id) {
        return customFilter;
      }
      return {
        ...customFilter,
        pinned: true,
      };
    });
    return { ...state, savedCustomFilters };
  },
  UNPINNED_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id } = payload;
    const savedCustomFilters = state.savedCustomFilters.map((customFilter) => {
      if (customFilter.id !== id) {
        return customFilter;
      }
      return {
        ...customFilter,
        pinned: false,
      };
    });
    return { ...state, savedCustomFilters };
  },
  FETCHED_CONTACT_CONVERSATIONS_COUNT: (state, { payload }) => ({
    ...state,
    contactConversations: {
      ...state.contactConversations,
      ...payload,
    },
  }),
  FETCHED_MY_CONVERSATIONS_COUNT: (state, { payload }) => ({
    ...state,
    myConversations: setCount(state.myConversations, payload.count),
  }),
  FETCHED_UNASSIGNED_CONVERSATIONS_COUNT: (state, { payload }) => ({
    ...state,
    unassignedConversations: setCount(
      state.unassignedConversations,
      payload.count
    ),
  }),
  FETCHED_OPEN_CONVERSATIONS_COUNT: (state, { payload }) => ({
    ...state,
    openConversations: setCount(state.openConversations, payload.count),
  }),
  FETCHED_UNDELIVERED_MESSAGES_CONVERSATIONS_COUNT: (state, { payload }) => ({
    ...state,
    undeliveredMessagesConversations: setCount(
      state.undeliveredMessagesConversations,
      payload.count
    ),
  }),
  FETCHED_BOT_INTERFERENCE_CONVERSATIONS_COUNT: (state, { payload }) => ({
    ...state,
    botInterferenceConversations: setCount(
      state.botInterferenceConversations,
      payload.count
    ),
  }),
  FETCHED_CUSTOM_FILTER_COUNT: (state, { payload }) => ({
    ...state,
    customFilter: setCount(state.customFilter, payload.count),
  }),
  FETCHED_SAVED_CUSTOM_FILTER_COUNT: (state, { payload }) => {
    const { id, count } = payload;
    const savedCustomFilters = updateSavedCustomFilters(state, id, {
      count,
    });
    return { ...state, savedCustomFilters };
  },
  STARTED_FETCHING_CONVERSATION: (state, { payload }) => {
    const conversationId = payload;
    const fetchingConversations = {
      ...state.fetchingConversations,
      [conversationId]: true,
    };
    return { ...state, fetchingConversations };
  },
  FETCHED_CONVERSATION: (state, { payload }) => {
    const conversation = payload;
    const fetchingConversations = {
      ...state.fetchingConversations,
      [conversation.id]: false,
    };
    const conversationById = {
      ...state.conversationById,
      [conversation.id]: conversation,
    };
    return { ...state, fetchingConversations, conversationById };
  },
  CONTACTS_MERGED: (state, { payload }) => {
    const { oldContactId, newContactId } = payload;
    const conversations = Object.values(state.conversationById);
    const isWithOldContact = (conversation) =>
      conversation.user_id === oldContactId;
    const changeToNewContact = (conversation) => ({
      ...conversation,
      user_id: newContactId,
    });
    const updatedConversations = conversations
      .filter(isWithOldContact)
      .map(changeToNewContact);
    const addToObjectById = (object, item) => ({ ...object, [item.id]: item });
    const newConversationById = updatedConversations.reduce(
      addToObjectById,
      state.conversationById
    );
    return { ...state, conversationById: newConversationById };
  },
  FETCHED_SAVED_CUSTOM_FILTERS_DETAILS: (state, { payload }) => {
    const savedCustomFilters = payload;
    const pinnedFiltersFromStorage = localStorage.getItem(
      'conversation-saved-custom-filters'
    );
    const pinnedFilters = pinnedFiltersFromStorage
      ? JSON.parse(pinnedFiltersFromStorage)
      : [];

    return {
      ...state,
      savedCustomFilters: savedCustomFilters.map(
        ({ id, config: filters, name }) => ({
          id,
          name,
          filters,
          count: 0,
          pinned: pinnedFilters.includes(id),
          ...defaultFilterStateWithUnreadIds,
        })
      ),
    };
  },
  CREATED_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id, config: filters, name } = payload;
    const savedCustomFilters = [
      ...state.savedCustomFilters,
      {
        id,
        name,
        filters,
        pinned: false,
        count: 0,
        ...defaultFilterStateWithUnreadIds,
      },
    ];
    return {
      ...state,
      savedCustomFilters,
    };
  },
  UPDATED_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const { id, name, config: filters } = payload;
    const savedCustomFilters = state.savedCustomFilters.map((customFilter) => {
      if (customFilter.id === id) {
        return {
          ...customFilter,
          name,
          filters,
        };
      }
      return customFilter;
    });

    return {
      ...state,
      savedCustomFilters,
    };
  },
  DELETED_SAVED_CUSTOM_FILTER: (state, { payload }) => {
    const deletedFilterId = payload;
    const savedCustomFilters = state.savedCustomFilters.filter(
      (customFilter) => customFilter.id !== deletedFilterId
    );

    return {
      ...state,
      savedCustomFilters,
    };
  },
  STARTED_FETCHING_CONTACT_ATTENDANCES: (state, { payload }) => {
    const fetchStatus = { fetching: true };
    return {
      ...state,
      attendanceFetchStatus: {
        ...state.attendanceFetchStatus,
        [payload.contactUlid]: fetchStatus,
      },
    };
  },
  FETCHED_CONTACT_ATTENDANCES: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    const attendanceFetchStatus = {
      ...state.attendanceFetchStatus,
      [payload.contactUlid]: fetchStatus,
    };
    const attendancesByContactUlid = {
      ...state.attendancesByContactUlid,
      [payload.contactUlid]: payload.attendances,
    };

    return { ...state, attendanceFetchStatus, attendancesByContactUlid };
  },
  FAILED_FETCHING_CONTACT_ATTENDANCES: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    return {
      ...state,
      attendanceFetchStatus: {
        ...state.attendanceFetchStatus,
        [payload.contactUlid]: fetchStatus,
      },
    };
  },

  STARTED_FETCHING_CONTACT_INTEGRATION_SYNCS: (state, { payload }) => {
    const fetchStatus = { fetching: true };
    return {
      ...state,
      integrationSyncFetchStatus: {
        ...state.integrationSyncFetchStatus,
        [payload.contactUuid]: fetchStatus,
      },
    };
  },
  FETCHED_CONTACT_INTEGRATION_SYNCS: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    const integrationSyncFetchStatus = {
      ...state.integrationSyncFetchStatus,
      [payload.contactUuid]: fetchStatus,
    };
    const integrationSyncByContactId = {
      ...state.integrationSyncByContactId,
      [payload.contactUuid]: payload.syncs,
    };
    return { ...state, integrationSyncFetchStatus, integrationSyncByContactId };
  },
  FAILED_FETCHING_CONTACT_INTEGRATION_SYNCS: (state, { payload }) => {
    const fetchStatus = { fetching: false };
    return {
      ...state,
      integrationSyncFetchStatus: {
        ...state.integrationSyncFetchStatus,
        [payload.contactUuid]: fetchStatus,
      },
    };
  },
  SET_RSVP_LINK: (state, { payload }) => {
    return {
      ...state,
      rsvpLink: payload,
    };
  },
};

const conversationsRouteReducer = (state = initialState, action) => {
  if (action && action.type && reductions[action.type]) {
    return reductions[action.type](state, action);
  }
  return state;
};

export default conversationsRouteReducer;
